import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  Compass,
  Stethoscope,
  Building2,
} from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import SubHero from "@/components/layout/SubHero";
import NextStep from "@/components/layout/NextStep";
import { Reveal, SectionOverline } from "@/components/landing/Shared";

const PROGRAMS = [
  {
    key: "foundations",
    icon: BookOpen,
    label: "Program 01",
    title: "Adaptive Physiology Foundations",
    lead:
      "An introductory experience for individuals who want to understand and begin applying the framework.",
    tone: "Educational · Approachable",
    to: "/programs/foundations",
  },
  {
    key: "guided-implementation",
    icon: Compass,
    label: "Program 02 · Flagship",
    title: "Adaptive Physiology Guided Implementation",
    lead:
      "Individualized implementation with structured coaching, assessment, and ongoing adaptation.",
    tone: "Personalized · Rigorous",
    to: "/programs/guided-implementation",
  },
  {
    key: "professional-consultation",
    icon: Stethoscope,
    label: "Program 03",
    title: "Professional Consultation",
    lead:
      "Support for clinicians, coaches, and healthcare professionals applying Adaptive Physiology in their own practice.",
    tone: "Collegial · Case-based",
    to: "/programs/professional-consultation",
  },
  {
    key: "organizational-partnerships",
    icon: Building2,
    label: "Program 04",
    title: "Organizational Partnerships",
    lead:
      "Integration of the methodology inside treatment centers, clinics, and health organizations.",
    tone: "Institutional · Integrative",
    to: "/programs/organizational-partnerships",
  },
];

const ProgramsHub = () => {
  return (
    <PageShell>
      <SubHero
        testId="programs-hero"
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Programs" }]}
        overline="Programs"
        headline="Experience Adaptive Physiology."
        headlineAccent="Four ways to apply it."
        lead="The framework is consistent. The level of support adapts to your needs. Every program applies the same Adaptive Loop: future demand, activation, matched intervention, parasympathetic recovery, recovery, and homeostasis."
        showAdaptiveLoop
        loopTestId="programs-hub-loop"
      />

      {/* Framework reference band */}
      <section
        data-testid="programs-framework-band"
        className="relative py-16 sm:py-20 forr-canvas-alt border-b border-line-soft"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-8 items-center">
            <div className="col-span-12 lg:col-span-8">
              <Reveal>
                <div className="forr-overline mb-4">The Consistent Framework</div>
                <h2 className="font-display forr-text-ink text-2xl sm:text-3xl md:text-4xl font-medium tracking-[-0.02em] leading-[1.1]">
                  Same loop. Different levels of support.
                </h2>
                <p className="mt-6 max-w-xl text-base sm:text-lg font-light forr-text-soft leading-relaxed">
                  Participants aren’t simply receiving coaching — they are
                  learning and applying the Adaptive Physiology™ framework
                  itself. Each program calibrates how much implementation happens
                  independently, together, or across an organization.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Four pathways */}
      <section
        data-testid="programs-pathways"
        className="relative py-24 sm:py-32 forr-canvas"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <SectionOverline
              number="→"
              label="Four Implementation Pathways"
              testId="programs-pathways-overline"
            />
          </Reveal>

          <div className="mt-14 grid sm:grid-cols-2 gap-6">
            {PROGRAMS.map((p, i) => (
              <Reveal delay={i * 0.06} key={p.key}>
                <Link
                  to={p.to}
                  data-testid={`programs-card-${p.key}`}
                  className="forr-card rounded-[24px] p-8 sm:p-10 h-full block group"
                >
                  <div className="flex items-start justify-between gap-4 mb-8">
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center"
                      style={{
                        backgroundColor: "var(--forr-teal-tint)",
                        color: "var(--forr-teal-deep)",
                      }}
                    >
                      <p.icon size={22} strokeWidth={1.5} />
                    </div>
                    <span className="text-[11px] uppercase tracking-[0.24em] forr-text-mute">
                      {p.label}
                    </span>
                  </div>
                  <h3 className="font-display forr-text-ink text-2xl sm:text-3xl font-medium tracking-tight mb-4">
                    {p.title}
                  </h3>
                  <p className="text-base font-light forr-text-soft leading-relaxed">
                    {p.lead}
                  </p>
                  <div className="mt-8 flex items-center justify-between border-t border-line-soft pt-6">
                    <span className="text-[11px] uppercase tracking-[0.24em] forr-text-teal-deep">
                      {p.tone}
                    </span>
                    <span className="inline-flex items-center gap-2 forr-text-teal-deep text-sm font-medium">
                      Open program
                      <ArrowRight
                        size={14}
                        strokeWidth={1.75}
                        className="transition-transform duration-300 group-hover:translate-x-1"
                      />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <NextStep
        overline="Return"
        title="Continue with the methodology"
        description="Every program is an implementation of the same framework. Revisit the loop that all four pathways share."
        to="/methodology"
        cta="Open methodology"
        testId="programs-hub-next"
      />
    </PageShell>
  );
};

export default ProgramsHub;
